/**
 * Every organization's backups, managed by the host. Called from a host operator's own session; the
 * server refuses it without Manage organizations, and refuses changing, running, downloading and
 * restoring in an organization the operator opens only as a technician.
 */
import type {
  BackupConfigDto,
  BackupStatusDto,
  BackupTestResult,
  HostTenantBackupDto,
  RestoreBackupResult,
  RunBackupResult,
  UpdateBackupConfigRequest
} from '../contracts.gen.js';
import { uploadFiles } from '../download.js';
import { ApiError, request } from './http.js';

const BASE = '/api/host/backups';

function reason(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return `The CRM could not be reached. (${error instanceof Error ? error.message : String(error)})`;
}

export function list(signal?: AbortSignal): Promise<HostTenantBackupDto[]> {
  return request<HostTenantBackupDto[]>(BASE, signal ? { signal } : {});
}

/** What the backup section calls, for one organization. */
export interface BackupApi {
  status(signal?: AbortSignal): Promise<BackupStatusDto>;
  save(req: UpdateBackupConfigRequest): Promise<BackupConfigDto>;
  /** Never throws: a probe that could not run comes back as a failed result. */
  test(req: UpdateBackupConfigRequest): Promise<BackupTestResult>;
  /** A failed run is a 200 carrying its reason; a transport failure is folded into the same shape. */
  run(): Promise<RunBackupResult>;
  restore(fileName: string, confirmPhrase: string, restartNow: boolean): Promise<RestoreBackupResult>;
  restoreUpload(file: File, confirmPhrase: string, restartNow: boolean, onProgress?: (fraction: number) => void): Promise<RestoreBackupResult>;
  cancelRestore(): Promise<void>;
  /** Restarts the organization, which applies a staged restore. */
  applyRestore(): Promise<void>;
  remove(fileName: string): Promise<void>;
  downloadPath(fileName: string): string;
}

export function forOrganization(tenantId: number): BackupApi {
  const at = `${BASE}/${tenantId}`;
  const notStaged = (error: unknown): RestoreBackupResult => ({
    success: false,
    summary: 'The restore was not staged.',
    error: reason(error, 'The restore could not be started.'),
    restartRequired: false
  });
  return {
    status: signal => request<BackupStatusDto>(at, signal ? { signal } : {}),
    save: req => request<BackupConfigDto>(`${at}/config`, { method: 'PUT', body: req }),
    async test(req) {
      try {
        return await request<BackupTestResult>(`${at}/test`, { method: 'POST', body: req });
      } catch (error) {
        return { success: false, summary: reason(error, 'The destination could not be tested.'), exists: false, writable: false, freeBytes: null, detail: null };
      }
    },
    async run() {
      try {
        return await request<RunBackupResult>(`${at}/run`, { method: 'POST', body: {} });
      } catch (error) {
        return { success: false, summary: 'The backup failed.', backup: null, error: reason(error, 'The backup could not be started.') };
      }
    },
    async restore(fileName, confirmPhrase, restartNow) {
      try {
        return await request<RestoreBackupResult>(`${at}/restore`, { method: 'POST', body: { fileName, confirmPhrase, restartNow } });
      } catch (error) {
        return notStaged(error);
      }
    },
    async restoreUpload(file, confirmPhrase, restartNow, onProgress) {
      try {
        const body = await uploadFiles(`${at}/restore/upload`, [file], onProgress, { confirmPhrase, restartNow: String(restartNow) });
        return JSON.parse(body) as RestoreBackupResult;
      } catch (error) {
        return notStaged(error);
      }
    },
    cancelRestore: () => request<void>(`${at}/restore`, { method: 'DELETE' }),
    applyRestore: () => request<void>(`${at}/restore/apply`, { method: 'POST', body: {} }),
    remove: fileName => request<void>(`${at}/${encodeURIComponent(fileName)}`, { method: 'DELETE' }),
    downloadPath: fileName => `${at}/download/${encodeURIComponent(fileName)}`
  };
}
